/**
 * @fileoverview Main news detail component for displaying a single news article with related content
 *
 * @see {@link src/components/news/topNews/TopNews} - Top news component
 * @see {@link src/components/news/relatedNews/index.jsx} - Related news component
 * @see {@link src/components/news/homeLine/NewsItem.jsx} - News item component
 */

/**
 * NewsItem interface representing a news article
 * @interface NewsItem
 * @property {string} id - Unique identifier for the news item
 * @property {string} title - Title of the news article
 * @property {string} slug - URL-friendly version of the title for routing
 * @property {string} [newsImageThumbnail] - Filename of the article's thumbnail image
 * @property {string} [content] - Full HTML content of the article
 * @property {string} [newsUrlOriginal] - Original source URL if from external site
 * @property {string} [externalNewsArticle] - Flag or URL for external articles
 * @property {string} [publishedAt] - Publication date in ISO format
 * @property {string} [description] - Brief summary or excerpt of the article
 *
 * @see {@link src/components/news/relatedNews/index.jsx} - Related news component
 * @see {@link src/app/(main)/(home)/news/[news]/page.tsx} - News detail page
 */

"use client";

import { Flex, Grid, Box, Text } from "@chakra-ui/react";
import Link from "next/link";

// Import for future use when social sharing is implemented
// @see JIRA ticket AVENTURE-123 for social sharing implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import NewsSocialBlock from "@/components/news/NewsSocialBlock";

import { BoostCard } from "../blog/boostCard/BoostCard";
import { BlogBreadcrumb } from "../blog/breadcrumb/Breadcrumb";
import { MainWrapper } from "../ui/box/MainWrapper";
import DefaultImage from "../ui/DefaultImage";

import AuthorInfo from "./AuthorInfo";
import ContentHeading from "./ContentHeading";
import MoreFrom from "./moreFrom";
import RelatedNews from "./relatedNews";
import TopNews from "./topNews";

// TODO: Set this to true when ready to enable the newsletter subscription feature in news articles
const SHOW_SUBSCRIPTION_BOX = false;
interface NewsItem {
  id: string;
  title: string;
  slug: string;
  newsImageThumbnail?: string;
  content?: string;
  newsUrlOriginal?: string;
  externalNewsArticle?: string;
  publishedAt?: string;
  description?: string;
  [key: string]: string | number | null | undefined;
}

/**
 * RelatedNewsResponse interface for pagination data structure
 * @interface RelatedNewsResponse
 * @property {NewsItem[]} newsItems - Array of related news articles
 * @property {number} currentPage - Current page number in pagination
 * @property {number} totalPages - Total number of available pages
 *
 * @see {@link src/components/news/relatedNews/index.jsx} - Component that consumes this interface
 */
interface RelatedNewsResponse {
  newsItems: NewsItem[];
  currentPage: number;
  totalPages: number;
}

/**
 * Props for the NewsDetail component
 * @interface NewsDetailProps
 * @property {NewsItem} oneNews - The main news article being displayed
 * @property {NewsItem[]} news - List of other news articles
 * @property {RelatedNewsResponse} relatedNews - Related news with pagination info
 */
interface NewsDetailProps {
  oneNews: NewsItem;
  news: NewsItem[];
  relatedNews: RelatedNewsResponse;
}

const NewsDetail = ({ oneNews, news, relatedNews }: NewsDetailProps) => {
  /**
   * Breadcrumb navigation items
   * @see src/components/blog/breadcrumb/Breadcrumb.tsx
   */
  const breadcrumbItems = [
    {
      title: "Home",
      path: "/",
      alt: "Page Home",
    },
    {
      title: "News",
      path: "/news",
      alt: "News Page",
    },
    {
      title: oneNews?.title,
      path: `/news/${oneNews?.slug}`,
      alt: "News Detail Page",
    },
  ];

  /**
   * Converts and sanitizes content from the API to proper HTML
   *
   * @function contentConvertor
   * @param {string | undefined} text - Raw content from the API
   * @returns {string} Sanitized HTML content ready for rendering
   */
  const contentConvertor = (text: string | undefined) => {
    if (!text) return "";

    // Ensure we have a fallback for the news images path
    const imgUrl =
      process.env.NEXT_PUBLIC_NEWS_IMAGES_PATH ||
      "https://images.aventure.vc/images/news";

    // First handle image paths
    let content = text;

    // Fix image paths - only replace specific news image paths
    content = content.replace(/src="\/images\//g, `src="${imgUrl}/`);
    // Removed overly broad replacement that was causing domain issues

    // Check if content is already HTML by looking for common HTML tags
    const hasHtml = /<[a-z][\s\S]*>/i.test(content);

    if (!hasHtml) {
      // Safely escape any HTML-like content in the text
      content = content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

      // Convert line breaks to paragraphs
      content = content
        // Split into paragraphs
        .split(/\n\s*\n/)
        // Remove empty paragraphs
        .filter((para) => para.trim().length > 0)
        // Wrap each paragraph
        .map((para) => `<p>${para.trim().replace(/\n/g, "<br />")}</p>`)
        .join("\n");
    } else {
      // Handle nested divs that might cause formatting issues
      // Remove empty paragraphs
      content = content.replace(/<p>\s*<\/p>/g, "");
      content = content.replace(/<p>\s*&nbsp;\s*<\/p>/g, "");

      // Remove excessive div nesting that might be in the content
      // This pattern looks for nested div.article-content tags that are common in imported content
      content = content.replace(
        /(<div class="article-content">\s*)+/g,
        '<div class="article-content">'
      );
      content = content.replace(/(<\/div>\s*){2,}/g, "</div>");

      // Ensure proper spacing between paragraphs by adding a class
      content = content.replace(/<p>/g, '<p class="news-paragraph">');

      // Clean up any excessive whitespace between tags
      content = content.replace(/>\s+</g, "> <");
    }

    return content;
  };

  const srcImg = oneNews?.newsImageThumbnail
    ? `${process.env.NEXT_PUBLIC_NEWS_IMAGES_PATH || "https://images.aventure.vc/images/news"}/${oneNews?.newsImageThumbnail}`
    : null;

  return (
    <MainWrapper customStyleOuter={{ mb: "40px" }} customStyleInner={{}}>
      <Flex flexDir={"column"} pt={"27px"} px={{ base: "24px", md: "0" }}>
        <BlogBreadcrumb resArr={breadcrumbItems} containerStyles={{}} />
        <AuthorInfo {...{ oneNews }} />

        <ContentHeading title={oneNews?.title} customStyles={{}} />

        <Grid
          templateColumns={{
            base: "minmax(0, 1fr)",
            lg: "2.5fr minmax(387px, 1fr)",
          }}
          columnGap={"60px"}
          rowGap={"30px"}
        >
          <Flex flexDir={"column"} w={"100%"} align={"center"}>
            {srcImg ? (
              <DefaultImage
                src={srcImg}
                options={{
                  width: 500,
                  height: 500,
                  loading: "lazy",
                  alt: "news image",
                  style: {
                    borderRadius: "12px",
                    objectFit: "cover",
                    marginBottom: "20px",
                  },
                }}
              />
            ) : null}
            {/* 
              Content container with custom styling for news articles
              - Applies consistent paragraph spacing
              - Handles nested content structures from various sources
              - Ensures proper styling for links, lists, and blockquotes
            */}
            <Box
              fontSize={"16px"}
              fontWeight={"400"}
              lineHeight={"1.35"}
              letterSpacing={"0.34px"}
              w="100%"
              sx={{
                // Add proper spacing between paragraphs
                p: {
                  marginBottom: "1.5rem",
                },
                // Special styling for paragraphs with our custom class
                ".news-paragraph": {
                  marginBottom: "1.5rem",
                  paddingBottom: "0.5rem",
                },
                // Style lists properly
                "ul, ol": {
                  paddingLeft: "2rem",
                  marginBottom: "1.5rem",
                },
                // Style links
                a: {
                  color: "blue.500",
                  textDecoration: "underline",
                  _hover: {
                    color: "blue.600",
                  },
                },
                // Style blockquotes
                blockquote: {
                  borderLeft: "4px solid #e2e8f0",
                  paddingLeft: "1rem",
                  fontStyle: "italic",
                  marginBottom: "1.5rem",
                },
                // Add spacing between divs
                ".article-content": {
                  marginBottom: "1.5rem",
                },
                // Ensure proper spacing between elements
                "* + *": {
                  marginTop: "0.5rem",
                },
              }}
              dangerouslySetInnerHTML={{
                __html: `${contentConvertor(oneNews?.content)}`,
              }}
            />

            {oneNews?.newsUrlOriginal ? (
              <Text
                mt={"24px"}
                alignSelf={"start"}
                fontSize={"16px"}
                fontWeight={"500"}
                lineHeight={"normal"}
                letterSpacing={"0.38px"}
              >
                <Link
                  href={
                    oneNews?.newsUrlOriginal ||
                    oneNews?.externalNewsArticle ||
                    ""
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer nofollow"}
                >
                  <Box
                    as={"span"}
                    fontWeight={"400"}
                    color={"blue"}
                    _hover={{ color: "grey" }}
                    textDecoration={"underline"}
                  >
                    View original article on techcrunch.com
                  </Box>
                </Link>
              </Text>
            ) : null}
            {/* Social sharing block is hidden */}
          </Flex>
          <Flex
            flexDir={"column"}
            rowGap={"20px"}
            display={{ base: "none", lg: "flex" }}
          >
            <TopNews {...{ topNews: news }} />
            <RelatedNews {...{ oneNews, relatedNews }} />
            {/* Newsletter subscription box - conditionally rendered based on feature flag */}
            {SHOW_SUBSCRIPTION_BOX && (
              <BoostCard
                style={{
                  mx: 0,
                  minW: "300px",
                  height: "fit-content",
                  p: "40px 0",
                }}
                styleFlexFlex={{
                  align: { base: "center", md: "unset", lg: "center" },
                }}
                styleFlexFlexFlex={{
                  w: { base: "81%", md: "unset", lg: "81%" },
                  maxW: { base: "81%", md: "unset", lg: "81%" },
                }}
                customBtnStyle={{
                  w: "100%",
                  minW: { base: "100%", md: "284px", lg: "100%" },
                }}
                backgroundImage='url("/images/cta_blog.png")'
                maxW={"90%"}
                isMin={true}
                title="Don't miss our latest news and updates. Subscribe to the newsletter"
                btnTitle={"Subscribe"}
              />
            )}
          </Flex>

          <MoreFrom {...{ techNews: news, oneNews }} />
        </Grid>
      </Flex>
    </MainWrapper>
  );
};

export default NewsDetail;
